import { Blueprint } from '@/components/builder/base/blueprints/Blueprint';
import { ValidatableBlueprint } from '@/components/builder/base/blueprints/ValidatableBlueprint';
import { HasWidth } from '@/components/builder/base/traits/HasWidth';
import { ValidationErrors } from '@/components/builder/base/types/ValidationErrors';
import { FormBuilderContract } from '@/components/builder/form';
import { BlueprintDefinition } from '@/components/builder/form/blueprints/BlueprintDefinition';
import { CustomErrorBlueprint } from '@/components/builder/form/blueprints/CustomErrorBlueprint';
import { ReadonlyBlueprint } from '@/components/builder/form/blueprints/ReadonlyBlueprint';
import { RequiredBlueprint } from '@/components/builder/form/blueprints/RequiredBlueprint';
import { VisibleBlueprint } from '@/components/builder/form/blueprints/VisibleBlueprint';
import { Entry, entry, instanceOfEntry } from '@/components/builder/form/entries/Entry';
import { ValidEntry } from '@/components/builder/form/entries/ValidEntry';
import { AlwaysChoice } from '@/components/builder/form/enums/AlwaysChoice';
import { InternallyChoice } from '@/components/builder/form/enums/InternallyChoice';
import { NeverChoice } from '@/components/builder/form/enums/NeverChoice';
import { WhenChoice } from '@/components/builder/form/enums/WhenChoice';
import { EntryFactory } from '@/components/builder/form/traits/EntryFactory';
import { HasFilter } from '@/components/builder/form/traits/HasFilter';
import { HasHelp } from '@/components/builder/form/traits/HasHelp';
import { HasLabel } from '@/components/builder/form/traits/HasLabel';
import { HasPlaceholder } from '@/components/builder/form/traits/HasPlaceholder';
import { HasClientSideTriggers, HasServerSideTriggers, TriggerModel, TriggerMoments } from '@/components/builder/form/traits/HasTriggers';
import { ProcessCallback } from '@/components/builder/form/types/ProcessCallback';

export const Definition: BlueprintDefinition = {
    type: 'boolean',
    name: '[[[Tak/Nie]]]',
    icon: 'fa-check-square',
    group: 'primary',
    position: 3,
    disabled: true
};

export enum BooleanFieldTypes {
    Radio = 'Radio',
    Checkbox = 'Checkbox',
    Toggle = 'Toggle'
}

export class BooleanEntry extends ValidEntry<boolean>
{
    public type: string = Definition.type;
    public data: boolean = null;

    public constructor(data: any = null)
    {
        super();

        if (data !== null)
        {
            this.data = data;
        }
    }

    public async collect(blueprint: BooleanContract, form: FormBuilderContract, preprocess: ProcessCallback): Promise<Entry>
    {
        await preprocess(blueprint, this, form.blueprintId, form.entryId);

        return entry(this.data ?? blueprint.defaultValue);
    }

    public validate(blueprint: BooleanContract, form: FormBuilderContract): boolean
    {
        this.errors = {};

        const value = this.data ?? blueprint.defaultValue;

        if (!form.expressions.readonly(blueprint, true) && form.expressions.visible(blueprint, true))
        {
            if (form.expressions.required(blueprint) && (value === null || value === false))
            {
                this.errors.value = [`[[[Pole "%0" jest wymagane.|||${form.localization.translate(blueprint.label)}]]]`];
            }
            else if (form.expressions.customError(blueprint))
            {
                this.errors.custom = [form.expressions.customErrorMessage(blueprint, form)];
            }
        }

        return this.valid();
    }

    private getValues(blueprint: BooleanContract): boolean
    {
        return this.data ?? blueprint.defaultValue;
    }
}

export const instanceOfBooleanEntry = (object: any): object is BooleanEntry =>
{
    return instanceOfEntry(object) && 'type' in object && object.type === Definition.type;
};

export interface BooleanContract extends Blueprint, VisibleBlueprint, ReadonlyBlueprint, RequiredBlueprint, CustomErrorBlueprint, HasLabel, HasPlaceholder, HasHelp, HasWidth, HasFilter, HasServerSideTriggers, HasClientSideTriggers
{
    fieldType: BooleanFieldTypes;
    defaultValue: boolean;
    trueValueText: Record<string, string>;
    falseValueText: Record<string, string>;
}

export class BooleanType implements BooleanContract, ValidatableBlueprint, EntryFactory<BooleanEntry>
{
    public id: string;
    public type: string;
    public name: string;
    public label: Record<string, string>;
    public showLabel: boolean;
    public placeholder: Record<string, string>;
    public help: Record<string, string>;
    public minWidth: number;
    public width: number;
    public fieldType: BooleanFieldTypes;
    public defaultValue: boolean;
    public trueValueText: Record<string, string>;
    public falseValueText: Record<string, string>;
    public visible: AlwaysChoice | NeverChoice | InternallyChoice | WhenChoice;
    public visibleWhen: string;
    public readonly: AlwaysChoice | NeverChoice | InternallyChoice | WhenChoice;
    public readonlyWhen: string;
    public required: AlwaysChoice | NeverChoice | WhenChoice;
    public requiredWhen: string;
    public customError: NeverChoice | WhenChoice;
    public customErrorWhen: string;
    public customErrorMessage: Record<string, string>;
    public errors: ValidationErrors;
    public showFilter: boolean;
    public serverSideTriggerMoments: TriggerMoments[] = [ TriggerMoments.onChange ];
    public serverSideTriggers: TriggerModel[];
    public clientSideTriggerMoments: TriggerMoments[] = [ TriggerMoments.onChange ];
    public clientSideTriggers: TriggerModel[];

    public constructor(id: string, name: string)
    {
        this.id = id;
        this.type = Definition.type;
        this.name = name;
        this.label = { 'pl-PL': 'Akceptacja' };
        this.showLabel = true;
        this.placeholder = {};
        this.defaultValue = false;
        this.help = {};
        this.fieldType = BooleanFieldTypes.Radio;
        this.minWidth = 1;
        this.width = 0;
        this.trueValueText = {};
        this.falseValueText = {};
        this.customError = NeverChoice.Never;
        this.customErrorWhen = null;
        this.customErrorMessage = {};
        this.readonly = NeverChoice.Never;
        this.readonlyWhen = null;
        this.required = NeverChoice.Never;
        this.requiredWhen = null;
        this.visible = AlwaysChoice.Always;
        this.visibleWhen = null;
        this.errors = {};
        this.showFilter = false;
        this.serverSideTriggers = [];
    }

    public setDefaultWidth(width: number): void
    {
        this.width = Math.min(3, Math.max(this.minWidth, width));
    }

    public createEntry(data: any): BooleanEntry
    {
        return new BooleanEntry(data);
    }

    public updateEntry(data: any, entry: BooleanEntry): BooleanEntry
    {
        return new BooleanEntry(data);
    }

    public validate(): Record<string, ValidationErrors>
    {
        this.errors = {};

        return {
            [this.name]: this.errors
        };
    }
}
